import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useAuth } from '@contexts/auth-provider';
import { ErrorPage } from '@shared/components/error-pages/ErrorPage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import type { Module, ModuleName } from './api/types/module.types';
import type {
  ModulesContextValue,
  ModulesProviderProps,
} from './ModulesProvider.types';

import { fetchModules } from './api/fetch-modules/fetchModules';
import { fetchSelectedModule } from './api/fetch-selected-module/fetchSelectedModule';
import { updateSelectedModule } from './api/update-selected-module/updateSelectedModule';

const ModulesContext = React.createContext<ModulesContextValue | undefined>(
  undefined
);

const ModulesProvider = React.memo(
  ({ children }: Readonly<ModulesProviderProps>): React.ReactElement => {
    const queryClient = useQueryClient();
    const { isAuthenticated } = useAuth();

    const {
      data: modules = [],
      isLoading: isModulesLoading,
      isError: isModulesError,
      error: modulesError,
    } = useQuery({
      queryKey: ['modules'],
      queryFn: fetchModules,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: isAuthenticated,
    });

    const shouldFetchSelectedModule = React.useMemo(
      () => isAuthenticated && modules.length > 1,
      [isAuthenticated, modules.length]
    );

    const {
      data: selectedModuleData,
      isLoading: isSelectedModuleLoading,
      isError: isSelectedModuleError,
      error: selectedModuleError,
    } = useQuery({
      queryKey: ['selectedModule'],
      queryFn: fetchSelectedModule,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: shouldFetchSelectedModule,
    });

    const selectedModule = React.useMemo<Module | null>(() => {
      if (modules.length === 1) {
        return modules[0];
      }
      if (!selectedModuleData?.name) return null;
      const found =
        modules.find(m => m.name === selectedModuleData.name) ?? null;
      return found;
    }, [modules, selectedModuleData]);

    // Fatal error: PUT failed and user has no previous module (new user).
    // Renders ErrorPage instead of children.
    const [isFatalError, setIsFatalError] = React.useState(false);

    const selectModuleMutation = useMutation({
      mutationFn: (moduleName: ModuleName) =>
        updateSelectedModule({ name: moduleName }),
      onMutate: async moduleName => {
        await queryClient.cancelQueries({ queryKey: ['selectedModule'] });

        const previousModule = queryClient.getQueryData(['selectedModule']);
        const selectedMod = modules.find(m => m.name === moduleName);
        if (selectedMod) {
          queryClient.setQueryData(['selectedModule'], selectedMod);
        }
        return { previousModule };
      },
      onError: (_error, _moduleName, context) => {
        if (context?.previousModule) {
          // Existing user — rollback to previous module + show flag message
          queryClient.setQueryData(['selectedModule'], context.previousModule);
          emitFlagMessage(
            'danger',
            'Failed to switch module. Please try again.',
            5000
          );
        } else {
          // New user — no previous selection to rollback to → fatal error
          setIsFatalError(true);
        }
      },
    });

    const selectModuleMutationRef = React.useRef(selectModuleMutation);
    selectModuleMutationRef.current = selectModuleMutation;

    const selectedModuleRef = React.useRef(selectedModule);
    selectedModuleRef.current = selectedModule;

    const selectModule = React.useCallback(
      async (moduleName: ModuleName): Promise<void> => {
        const mutation = selectModuleMutationRef.current;
        const currentModule = selectedModuleRef.current;
        if (mutation.isPending) {
          return;
        }
        if (currentModule?.name === moduleName) {
          return;
        }
        await mutation.mutateAsync(moduleName);
      },
      []
    );

    const hasMultipleModules = React.useMemo(
      () => modules.length > 1,
      [modules]
    );

    const combinedIsLoading = React.useMemo(
      () =>
        isModulesLoading ||
        (shouldFetchSelectedModule && isSelectedModuleLoading),
      [isModulesLoading, shouldFetchSelectedModule, isSelectedModuleLoading]
    );

    const contextValue: ModulesContextValue = React.useMemo(
      () => ({
        modules,
        selectedModule,
        hasMultipleModules,
        isModulesLoading: combinedIsLoading,
        isModulesError: isModulesError || isSelectedModuleError,
        modulesError: modulesError || selectedModuleError,
        selectModule,
        isSelecting: selectModuleMutation.isPending,
      }),
      [
        modules,
        selectedModule,
        hasMultipleModules,
        combinedIsLoading,
        isModulesError,
        isSelectedModuleError,
        modulesError,
        selectedModuleError,
        selectModule,
        selectModuleMutation.isPending,
      ]
    );

    const hasInitializationError =
      isModulesError || isSelectedModuleError || isFatalError;

    return (
      <ModulesContext value={contextValue}>
        {hasInitializationError ? (
          <ErrorPage type="internal-server-error" />
        ) : (
          children
        )}
      </ModulesContext>
    );
  }
);

ModulesProvider.displayName = 'ModulesProvider';

export { ModulesContext, ModulesProvider };
